import type { TFunction } from 'i18next'
import type { LongStayDiscountHint } from '@/shared/schemas/longStayDiscountHint.schema'

type NightlyPriceArgs = {
  price: string
  base?: string
  subsidy?: string
}

export type PropertyPriceGranularity = 'per_night' | 'per_stay'

export type PriceLabelOptions = {
  /** Brand ui_preferences.show_subsidy_mention - wording with “subvention”. Default off. */
  showSubsidyMention: boolean
  /**
   * Brand ui_preferences.show_unsubsidized_price - include base (+ optional %) in the
   * detail string. Prefer JSX strikethrough when rendering UI; this keeps a plain-text fallback.
   */
  showUnsubsidizedBreakdown?: boolean
  /** When true, use “À partir de …” (`*_from` / `*_from_plain`) keys. */
  hasVariablePricing?: boolean
  /** Required: drives /nuit vs /séjour (checkout multiplies by nights when per_night). */
  priceGranularity: PropertyPriceGranularity
}

/** /nuit when per_night; /séjour when per_stay. Booking mode never overrides. */
export function resolvePriceUnit(
  t: TFunction,
  priceGranularity: PropertyPriceGranularity,
): string {
  return priceGranularity === 'per_stay'
    ? t('reservation.properties.price_unit.stay')
    : t('reservation.properties.price_unit.night')
}

/** One-line teaser of the long-stay rule in force (« 1 nuit offerte dès 7 nuits »). */
export function longStayHintLabel(
  t: TFunction,
  hint: LongStayDiscountHint,
  format: { money: (cents: number) => string; percent: (fraction: number) => string },
): string {
  const threshold = hint.threshold_nights
  if (hint.discount_type === 'percent' && hint.percent_basis_points != null) {
    return t('price_breakdown.long_stay_hint.percent', {
      percent: format.percent(hint.percent_basis_points / 10_000),
      threshold,
    })
  }
  if (hint.discount_type === 'fixed_amount' && hint.amount_cents != null) {
    return t('price_breakdown.long_stay_hint.fixed_amount', { amount: format.money(hint.amount_cents), threshold })
  }
  if (hint.discount_type === 'nightly_rate' && hint.nightly_price_cents != null) {
    return t('price_breakdown.long_stay_hint.nightly_rate', { amount: format.money(hint.nightly_price_cents), threshold })
  }
  if (hint.discount_type === 'free_nights' && hint.free_nights_count != null) {
    const key = hint.free_nights_mode === 'extension' ? 'extension' : 'free_nights'

    return t(`price_breakdown.long_stay_hint.${key}`, { count: hint.free_nights_count, threshold })
  }

  return ''
}

function cardOrSummaryKey(
  stem: 'reservation.properties.card.nightly' | 'reservation.properties.detail.summary_price' | 'home.showcase.nightly',
  { showSubsidyMention, hasVariablePricing = false }: Pick<PriceLabelOptions, 'showSubsidyMention' | 'hasVariablePricing'>,
): string {
  if (hasVariablePricing) {
    return showSubsidyMention ? `${stem}_from` : `${stem}_from_plain`
  }
  return showSubsidyMention ? stem : `${stem}_plain`
}

export function propertyCardNightlyLabel(
  t: TFunction,
  price: string,
  {
    showSubsidyMention,
    hasVariablePricing = false,
    priceGranularity,
  }: Pick<PriceLabelOptions, 'showSubsidyMention' | 'hasVariablePricing' | 'priceGranularity'>,
): string {
  const key = cardOrSummaryKey('reservation.properties.card.nightly', {
    showSubsidyMention,
    hasVariablePricing,
  })
  return t(key, {
    price,
    unit: resolvePriceUnit(t, priceGranularity),
  })
}

export function propertyDetailNightlyLabel(
  t: TFunction,
  args: NightlyPriceArgs,
  {
    showSubsidyMention,
    showUnsubsidizedBreakdown = false,
    hasVariablePricing = false,
    priceGranularity,
  }: PriceLabelOptions,
): string {
  const unit = resolvePriceUnit(t, priceGranularity)

  if (
    showUnsubsidizedBreakdown &&
    args.base != null &&
    showSubsidyMention &&
    args.subsidy != null
  ) {
    const key = hasVariablePricing
      ? 'reservation.properties.detail.nightly_from_with_base'
      : 'reservation.properties.detail.nightly_with_base'
    return t(key, {
      price: args.price,
      base: args.base,
      subsidy: args.subsidy,
      unit,
    })
  }

  if (showUnsubsidizedBreakdown && args.base != null) {
    const key = hasVariablePricing
      ? 'reservation.properties.detail.nightly_from_with_base_plain'
      : 'reservation.properties.detail.nightly_with_base_plain'
    return t(key, {
      price: args.price,
      base: args.base,
      unit,
    })
  }

  if (hasVariablePricing) {
    const key = showSubsidyMention
      ? 'reservation.properties.detail.nightly_from'
      : 'reservation.properties.detail.nightly_from_plain'
    return t(key, { price: args.price, unit })
  }

  const key = showSubsidyMention
    ? 'reservation.properties.detail.nightly'
    : 'reservation.properties.detail.nightly_plain'
  return t(key, { price: args.price, unit })
}

export function propertyPerNightLabel(
  t: TFunction,
  price: string,
  {
    showSubsidyMention,
    hasVariablePricing = false,
    priceGranularity,
  }: Pick<PriceLabelOptions, 'showSubsidyMention' | 'hasVariablePricing' | 'priceGranularity'>,
): string {
  const key = cardOrSummaryKey('reservation.properties.detail.summary_price', {
    showSubsidyMention,
    hasVariablePricing,
  })
  return t(key, {
    price,
    unit: resolvePriceUnit(t, priceGranularity),
  })
}

export function homeShowcaseNightlyLabel(
  t: TFunction,
  price: string,
  {
    showSubsidyMention,
    hasVariablePricing = false,
    priceGranularity,
  }: Pick<PriceLabelOptions, 'showSubsidyMention' | 'hasVariablePricing' | 'priceGranularity'>,
): string {
  const key = cardOrSummaryKey('home.showcase.nightly', {
    showSubsidyMention,
    hasVariablePricing,
  })
  return t(key, {
    price,
    unit: resolvePriceUnit(t, priceGranularity),
  })
}
